/* Pure CMS-field coercions, shared by every dark-mode producer. The folder
   invariant is ZERO imports, which these satisfy — so this is the one site both a
   DDS organism and an app adapter can reach (an app adapter cannot import `lib/`
   into DDS, and DDS cannot import `lib/` at all). Each reader takes `unknown`:
   these values come off a CMS response, not a typed caller, and nothing here
   throws. ⚠ Client modules import this file, so what it reaches ships to the
   browser. */

/** A text field's content, or "" for anything that is not text with ink in it. */
export function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Authored text with the Dynamic Pricing resolver applied, or the authored value
 * when the field is blank, there is no resolver, or the resolver throws / returns
 * a non-string. The dynamic token is NOT parsed here — an unresolved `[<TOKEN>]`
 * passes through so a caller's own refusal fires. The ONE copy of this rule, so a
 * widened fallback cannot drift between the producers that run the resolver
 * (WAT-12451).
 */
export function applyResolver(value: unknown, resolveDynamic?: (value: string) => string): string {
  const authored = text(value);

  if (authored === "" || !resolveDynamic) {
    return authored;
  }

  try {
    const resolved = resolveDynamic(authored);
    return typeof resolved === "string" ? resolved.trim() : authored;
  } catch {
    return authored;
  }
}

/** Whether the value is a blok — an object with fields, not a list and not null. */
export function isBlock(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * A repeatable field's entries, coerced in ONE place for every repeatable.
 * ⚠ A lone entry does NOT arrive as a one-element array — `sb-fixer.ts`
 * collapses it to the bare object, and this is what undoes that; returning
 * `[]` would drop the row silently. See design-conflicts, "Why `list()` is
 * the coercion and the schema keeps its un-prefixed names" (WAT-12200).
 */
export function list<T>(value: unknown): T[] {
  if (Array.isArray(value)) {
    return value as T[];
  }

  return isBlock(value) ? [value as T] : [];
}

/** An asset field's URL. A Storyblok Asset arrives as `{ filename }` and a
 *  plain URL field as the string; the same field is authored both ways across
 *  the feeds, so either shape is accepted rather than rendering a blank image. */
export function assetUrl(value: unknown): string {
  if (typeof value === "string") {
    return value.trim();
  }

  return isBlock(value) ? text(value.filename) : "";
}

/* Enough of HTML to cover what the SVOD feed actually publishes. The feed
   carries entity-encoded names (`Max&#8482;`), and the legacy cards pass them
   straight into `alt`, so a screen reader announces "Max ampersand hash 8482"
   (dtv-plan-card-v2.tsx:604). Decoded here instead, for every producer. */
const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  trade: "™",
  reg: "®",
  copy: "©",
  deg: "°"
};

/**
 * `String.fromCodePoint` THROWS a RangeError above 0x10FFFF, so `&#999999999;`
 * in one feed name would escape to a caller's catch and drop the whole card.
 * An out-of-range reference is left as the author wrote it instead.
 *
 * Also refused — left as written rather than emitted — are NUL (`&#0;`) and the
 * lone surrogates `&#xD800;`–`&#xDFFF;`: `fromCodePoint` accepts both without
 * throwing, but neither is a usable character in alt text (WAT-12451, minor-3).
 */
function codePoint(match: string, value: number): string {
  if (
    !Number.isInteger(value) ||
    value <= 0 ||
    value > 0x10ffff ||
    (value >= 0xd800 && value <= 0xdfff)
  ) {
    return match;
  }
  return String.fromCodePoint(value);
}

/** Decodes the decimal, hex and named HTML entities the SVOD feed publishes, so
 *  `alt` carries the character and not the raw entity. Left untouched when there
 *  is no `&` to decode. */
export function decodeEntities(value: string): string {
  if (!value || value.indexOf("&") === -1) {
    return value || "";
  }
  return value
    .replace(/&#x([0-9a-f]+);/gi, (m, hex) => codePoint(m, parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (m, dec) => codePoint(m, parseInt(dec, 10)))
    .replace(/&([a-z]+);/gi, (m, name) => {
      /* `hasOwnProperty`, not a bare lookup: `&constructor;` would otherwise
         resolve an inherited key and splice the Object constructor into alt. */
      const key = String(name).toLowerCase();
      if (!Object.prototype.hasOwnProperty.call(NAMED_ENTITIES, key)) {
        return m;
      }
      return NAMED_ENTITIES[key];
    });
}
